/**
 * Форма новой библиотеки: название, категория (из существующих) или новая
 * категория строкой, заметка.
 *
 * Категории — данные, а не константа в коде: «нужна ещё одна» не должно
 * упираться в правку файла, поэтому новая заводится прямо отсюда.
 *
 * Режим правки: с `libraryId` форма сохраняет через `updateLibrary`, поля
 * берёт из `initial`. Создание и правка — одни и те же поля, две формы
 * разошлись бы.
 *
 * Одна форма на два места: экран «Библиотеки» и «В библиотеку» у пина. Две
 * копии разошлись бы на первой же правке полей.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";

import { btn, btnPrimary, ErrorLine, input, label } from "@/components/trends/ui";
import { createLibrary, libraryKeys, loadLibraries, updateLibrary } from "@/lib/library";

const NEW = "__new__";

export function LibraryCreateForm({
  defaultName = "",
  defaultCategory = "",
  submitLabel = "Создать",
  compact = false,
  initial,
  libraryId,
  onCreated,
  onCancel,
}: {
  defaultName?: string;
  /** Код категории по умолчанию; нет такой — первая из списка. */
  defaultCategory?: string;
  submitLabel?: string;
  /** Компактно — в оверлее пина: без заметки, поля столбиком. */
  compact?: boolean;
  /** Значения для правки; категория по умолчанию — `initial.category`. */
  initial?: { name: string; category: string; note: string };
  /** Задан — правим эту библиотеку, а не создаём новую. */
  libraryId?: number;
  /** Зовётся и после правки. */
  onCreated: (id: number, name: string) => void | Promise<void>;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries });
  const categories = list.data?.categories ?? [];

  const editing = libraryId !== undefined;
  const startCategory = initial?.category ?? defaultCategory;
  const [name, setName] = useState(initial?.name ?? defaultName);
  const [picked, setPicked] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [note, setNote] = useState(initial?.note ?? "");

  // Выбор по умолчанию считается от загруженного списка, а не замораживается
  // в первом рендере, когда список ещё пуст.
  const fallback = categories.some((c) => c.code === startCategory)
    ? startCategory
    : (categories[0]?.code ?? NEW);
  const category = picked ?? fallback;
  const isNew = category === NEW;

  const create = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        category: isNew ? "" : category,
        category_name: isNew ? categoryName.trim() : "",
        note: note.trim(),
      };
      if (libraryId !== undefined) {
        const got = await updateLibrary(libraryId, body);
        return got.library.id;
      }
      return (await createLibrary(body)).id;
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: libraryKeys.list });
      if (libraryId !== undefined) await queryClient.invalidateQueries({ queryKey: libraryKeys.one(libraryId) });
      await onCreated(id, name.trim());
    },
  });

  const ready = name.trim() !== "" && (!isNew || categoryName.trim() !== "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && !create.isPending) create.mutate();
  };

  const field = compact ? "w-full" : "";

  return (
    <form onSubmit={submit} className="grid gap-2" data-annot={editing ? "правка библиотеки" : "новая библиотека"}>
      <div className={compact ? "grid gap-2" : "flex flex-wrap items-end gap-2"}>
        <label className="grid gap-1">
          <span className={label}>название</span>
          <input
            className={`${input} ${compact ? field : "w-[280px]"}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="например, «Мраморные лестницы»"
            maxLength={160}
            required
          />
        </label>
        <label className="grid gap-1">
          <span className={label}>категория</span>
          <select
            className={`${input} ${compact ? field : "w-[200px]"}`}
            value={category}
            onChange={(e) => setPicked(e.target.value)}
            disabled={list.isPending}
          >
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
            <option value={NEW}>— новая категория…</option>
          </select>
        </label>
        {isNew ? (
          <label className="grid gap-1">
            <span className={label}>новая категория</span>
            <input
              className={`${input} ${compact ? field : "w-[200px]"}`}
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="название категории"
              maxLength={120}
              required
            />
          </label>
        ) : null}
        {compact ? null : (
          <label className="grid gap-1">
            <span className={label}>заметка</span>
            <input
              className={`${input} w-[280px]`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="о чём эта библиотека"
              maxLength={500}
            />
          </label>
        )}
        <div className="flex gap-2">
          <button type="submit" className={btnPrimary} disabled={!ready || create.isPending}>
            {editing ? <Check size={14} /> : <Plus size={14} />}
            {create.isPending ? (editing ? "Сохраняю…" : "Создаю…") : editing ? "Сохранить" : submitLabel}
          </button>
          {onCancel ? (
            <button type="button" className={btn} onClick={onCancel}>
              Отмена
            </button>
          ) : null}
        </div>
      </div>
      <ErrorLine error={list.error ?? create.error} />
    </form>
  );
}
